// =============================================================================
// BP-07-03: ResourceFilters Component
// =============================================================================

import { useState } from 'react';
import { X, ChevronDown, Filter, RotateCcw } from 'lucide-react';
import { useResourceCategories } from '@/features/resources/hooks/useResourceCategories';
import { useDiagnoses } from '@/features/diagnoses/hooks/useDiagnosis';
import { useResourceFilterStore, useActiveFilterCount } from '@/features/resources/stores/resourceStore';
import type { ResourceType, LifeStage } from '@/features/resources/types/resourcesTypes';
import { RESOURCE_TYPE_LABELS, LIFE_STAGE_LABELS } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface ResourceFiltersProps {
  variant?: 'sidebar' | 'inline' | 'modal';
  showCategoryFilter?: boolean;
  showDiagnosisFilter?: boolean;
  showTypeFilter?: boolean;
  showLifeStageFilter?: boolean;
  showAgeFilter?: boolean;
  onClose?: () => void;
}

// -----------------------------------------------------------------------------
// Filter Section Component
// -----------------------------------------------------------------------------

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function FilterSection({ title, children, defaultOpen = true }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-warm-gray/10 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 text-left"
      >
        <span className="font-medium text-navy">{title}</span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-warm-gray transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      {isOpen && <div className="pb-4">{children}</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Checkbox Option
// -----------------------------------------------------------------------------

interface CheckboxOptionProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  count?: number;
}

function CheckboxOption({ label, checked, onChange, count }: CheckboxOptionProps) {
  return (
    <label className="flex items-center gap-2 py-1.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-warm-gray/30 text-sage focus:ring-sage/20"
      />
      <span className="flex-1 text-sm text-navy group-hover:text-sage-dark">{label}</span>
      {count !== undefined && (
        <span className="text-xs text-warm-gray">({count})</span>
      )}
    </label>
  );
}

// -----------------------------------------------------------------------------
// Radio Option
// -----------------------------------------------------------------------------

interface RadioOptionProps {
  name: string;
  value: string;
  label: string;
  checked: boolean;
  onChange: () => void;
}

function RadioOption({ name, value, label, checked, onChange }: RadioOptionProps) {
  return (
    <label className="flex items-center gap-2 py-1.5 cursor-pointer group">
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 border-warm-gray/30 text-sage focus:ring-sage/20"
      />
      <span className="text-sm text-navy group-hover:text-sage-dark">{label}</span>
    </label>
  );
}

// -----------------------------------------------------------------------------
// Active Filters Tags
// -----------------------------------------------------------------------------

export function ActiveFiltersTags() {
  const filters = useResourceFilterStore();
  const { data: categories } = useResourceCategories();
  const { data: diagnoses } = useDiagnoses();

  const activeFilters: { key: string; label: string; onRemove: () => void }[] = [];

  if (filters.categorySlug) {
    const category = categories?.find((c) => c.slug === filters.categorySlug);
    activeFilters.push({
      key: 'category',
      label: category?.name || filters.categorySlug,
      onRemove: () => filters.setCategorySlug(null),
    });
  }

  if (filters.diagnosisSlug) {
    const diagnosis = diagnoses?.find((d) => d.slug === filters.diagnosisSlug);
    activeFilters.push({
      key: 'diagnosis',
      label: diagnosis?.name || filters.diagnosisSlug,
      onRemove: () => filters.setDiagnosisSlug(null),
    });
  }

  if (filters.resourceType) {
    activeFilters.push({
      key: 'type',
      label: RESOURCE_TYPE_LABELS[filters.resourceType],
      onRemove: () => filters.setResourceType(null),
    });
  }

  if (filters.lifeStage) {
    activeFilters.push({
      key: 'stage',
      label: LIFE_STAGE_LABELS[filters.lifeStage],
      onRemove: () => filters.setLifeStage(null),
    });
  }

  filters.tags.forEach((tag) => {
    activeFilters.push({
      key: `tag-${tag}`,
      label: tag,
      onRemove: () => filters.removeTag(tag),
    });
  });

  if (activeFilters.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {activeFilters.map((filter) => (
        <span
          key={filter.key}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sage/10 text-sage-dark text-sm"
        >
          {filter.label}
          <button
            onClick={filter.onRemove}
            className="p-0.5 rounded-full hover:bg-sage/20 transition-colors"
            aria-label={`Remove ${filter.label} filter`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <button
        onClick={filters.clearFilters}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-sm text-warm-gray hover:text-terracotta transition-colors"
      >
        <RotateCcw className="w-3 h-3" />
        Clear all
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ResourceFilters Component
// -----------------------------------------------------------------------------

export function ResourceFilters({
  variant = 'sidebar',
  showCategoryFilter = true,
  showDiagnosisFilter = true,
  showTypeFilter = true,
  showLifeStageFilter = true,
  showAgeFilter = false,
  onClose,
}: ResourceFiltersProps) {
  const filters = useResourceFilterStore();
  const activeCount = useActiveFilterCount();
  const { data: categories, isLoading: loadingCategories } = useResourceCategories();
  const { data: diagnoses, isLoading: loadingDiagnoses } = useDiagnoses();

  const resourceTypes: ResourceType[] = ['article', 'guide', 'video', 'checklist', 'template'];
  const lifeStages: LifeStage[] = ['crisis', 'early_intervention', 'school_age', 'transition', 'adult'];

  const content = (
    <div className="space-y-1">
      {/* Category Filter */}
      {showCategoryFilter && (
        <FilterSection title="Category">
          {loadingCategories ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-6 bg-cream-dark rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              <RadioOption
                name="category"
                value=""
                label="All Categories"
                checked={!filters.categorySlug}
                onChange={() => filters.setCategorySlug(null)}
              />
              {categories?.map((category) => (
                <RadioOption
                  key={category.id}
                  name="category"
                  value={category.slug}
                  label={category.name}
                  checked={filters.categorySlug === category.slug}
                  onChange={() => filters.setCategorySlug(category.slug)}
                />
              ))}
            </div>
          )}
        </FilterSection>
      )}

      {/* Diagnosis Filter */}
      {showDiagnosisFilter && (
        <FilterSection title="Diagnosis">
          {loadingDiagnoses ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-6 bg-cream-dark rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              <RadioOption
                name="diagnosis"
                value=""
                label="All Diagnoses"
                checked={!filters.diagnosisSlug}
                onChange={() => filters.setDiagnosisSlug(null)}
              />
              {diagnoses?.map((diagnosis) => (
                <RadioOption
                  key={diagnosis.id}
                  name="diagnosis"
                  value={diagnosis.slug}
                  label={diagnosis.name}
                  checked={filters.diagnosisSlug === diagnosis.slug}
                  onChange={() => filters.setDiagnosisSlug(diagnosis.slug)}
                />
              ))}
            </div>
          )}
        </FilterSection>
      )}

      {/* Type Filter */}
      {showTypeFilter && (
        <FilterSection title="Content Type">
          <div className="space-y-1">
            <RadioOption
              name="type"
              value=""
              label="All Types"
              checked={!filters.resourceType}
              onChange={() => filters.setResourceType(null)}
            />
            {resourceTypes.map((type) => (
              <RadioOption
                key={type}
                name="type"
                value={type}
                label={RESOURCE_TYPE_LABELS[type]}
                checked={filters.resourceType === type}
                onChange={() => filters.setResourceType(type)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Life Stage Filter */}
      {showLifeStageFilter && (
        <FilterSection title="Life Stage">
          <div className="space-y-1">
            <RadioOption
              name="lifestage"
              value=""
              label="All Stages"
              checked={!filters.lifeStage}
              onChange={() => filters.setLifeStage(null)}
            />
            {lifeStages.map((stage) => (
              <RadioOption
                key={stage}
                name="lifestage"
                value={stage}
                label={LIFE_STAGE_LABELS[stage]}
                checked={filters.lifeStage === stage}
                onChange={() => filters.setLifeStage(stage)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Age Range Filter */}
      {showAgeFilter && (
        <FilterSection title="Age Range" defaultOpen={false}>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="100"
                placeholder="Min"
                value={filters.ageRange?.[0] ?? ''}
                onChange={(e) => {
                  const min = e.target.value ? parseInt(e.target.value) : 0;
                  const max = filters.ageRange?.[1] ?? 100;
                  filters.setAgeRange([min, max]);
                }}
                className="w-20 px-2 py-1 text-sm rounded border border-warm-gray/20 focus:border-sage focus:ring-1 focus:ring-sage/20"
              />
              <span className="text-warm-gray">to</span>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="Max"
                value={filters.ageRange?.[1] ?? ''}
                onChange={(e) => {
                  const min = filters.ageRange?.[0] ?? 0;
                  const max = e.target.value ? parseInt(e.target.value) : 100;
                  filters.setAgeRange([min, max]);
                }}
                className="w-20 px-2 py-1 text-sm rounded border border-warm-gray/20 focus:border-sage focus:ring-1 focus:ring-sage/20"
              />
            </div>
            {filters.ageRange && (
              <button
                onClick={() => filters.setAgeRange(null)}
                className="text-xs text-warm-gray hover:text-terracotta"
              >
                Clear age filter
              </button>
            )}
          </div>
        </FilterSection>
      )}

      {/* Clear All Button */}
      {activeCount > 0 && (
        <div className="pt-4">
          <button
            onClick={filters.clearFilters}
            className="w-full py-2 px-4 text-sm font-medium text-warm-gray hover:text-terracotta border border-warm-gray/20 rounded-lg hover:border-terracotta/30 transition-colors"
          >
            Clear All Filters ({activeCount})
          </button>
        </div>
      )}
    </div>
  );

  // Sidebar variant
  if (variant === 'sidebar') {
    return (
      <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-medium text-navy">Filters</h3>
          {activeCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium text-white bg-sage rounded-full">
              {activeCount}
            </span>
          )}
        </div>
        {content}
      </div>
    );
  }

  // Modal variant
  if (variant === 'modal') {
    return (
      <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-warm-gray/10">
          <h3 className="font-display font-medium text-navy flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
            {activeCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium text-white bg-sage rounded-full">
                {activeCount}
              </span>
            )}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-cream-dark transition-colors"
          >
            <X className="w-5 h-5 text-warm-gray" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{content}</div>
        <div className="p-4 border-t border-warm-gray/10">
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 bg-sage text-white font-medium rounded-lg hover:bg-sage-dark transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    );
  }

  // Inline variant
  return <div className="p-4">{content}</div>;
}

export default ResourceFilters;

// Filters Modal Component
export function FiltersModal({ 
  isOpen, 
  onClose,
  children 
}: { 
  isOpen: boolean; 
  onClose: () => void;
  children?: React.ReactNode;
}) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold text-lg">Filters</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            ✕
          </button>
        </div>
        <div className="p-4">
          {children || <ResourceFilters />}
        </div>
      </div>
    </div>
  );
}

